import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';

// INCIDENTE 2026-09-07 (Ann): las rutas de personal se abrían por «≠ cliente» → cualquier rol no-cliente
// (p. ej. el externo 'colaborador') entraba escribiendo la URL (/admin/cliente-nuevo?id=…). Ahora se abren
// por LISTA BLANCA de roles de personal. El colaborador SOLO entra en /admin (su panel acotado a leads).
// La cerradura real está en el servidor (get_admin_user rechaza cliente y colaborador); esto evita pintar
// pantallas de personal a quien no le corresponden.
const ROLES_PERSONAL = ['admin', 'editor', 'gestor', 'fisioterapeuta', 'direccion_tecnica', 'finanzas', 'editor_comercial'];
const esPersonal = (u) => !!u && ROLES_PERSONAL.includes(u.role);
const esColaborador = (u) => !!u && u.role === 'colaborador';
import './i18n'; // inicializa i18next ANTES de renderizar (F0 bilingüe ES/CA)
import { LanguageProvider } from './context/LanguageContext';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import ClientDashboard from './pages/ClientDashboard';
import CanalPilarDashboard from './pages/CanalPilarDashboard';
import MiPerfil from './pages/MiPerfil';
import ResetPassword from './pages/ResetPassword';
import WordpressTokenBridge from './pages/WordpressTokenBridge';
import CanalSubir from './pages/CanalSubir';
import VisionClienteTab, { ReproductorPreview } from './components/admin/canal/VisionClienteTab';
import TabletCuestionario from './pages/TabletCuestionario';
import ClientNewProfile from './pages/ClientNewProfile';
import EmpleadoFicha from './pages/EmpleadoFicha';
import ClientProfileErrorBoundary from './components/ClientProfileErrorBoundary';
import CobrosPendientesPage from './pages/CobrosPendientesPage';
import ControlAsistenciaPage from './pages/ControlAsistenciaPage';
import VistaSalaShell from './pages/vista-en-sala/VistaSalaShell';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { debeCerrarPorExpulsion, tokenDePeticion } from './utils/sesionExpulsada';
import { VideoUploadProvider } from './context/VideoUploadContext';
import GlobalUploadProgress from './components/admin/canal/GlobalUploadProgress';
import OnboardingBienvenida from './components/client/OnboardingBienvenida';
import FotoPerfilObligatoria from './components/client/FotoPerfilObligatoria';
import AltaFunnelCanal from './components/client/AltaFunnelCanal';
import { VerificarEmail, PagoResultado, CompletaPerfil } from './components/client/PantallasAltaCanal';
import LegalCanal from './components/client/LegalCanal';
import RegistroCanal from './components/client/RegistroCanal';
import RecuperacionPasswordDemo from './components/client/RecuperacionPasswordDemo';
import ClubAlta from './components/client/ClubAlta';
import CanalLanding from './components/client/CanalLanding';
import CanalAltaForm from './components/client/CanalAltaForm';
import InscripcionCanal from './components/client/InscripcionCanal';
import InvitacionCanal from './components/client/InvitacionCanal';
import { confirmarPago } from './api/canalRegistro';
import './App.css';

export { API } from './api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── ENRUTADO POR HOST (host-based routing) ────────────────────────────────
// canal.metodopilardominguez.com  → modo CLIENTE (solo experiencia de cliente).
// crm.metodopilardominguez.com / localhost → modo STAFF (back-office, como siempre).
// En modo CLIENTE el back-office NO se monta (no basta con ocultar el menú).
// El bloqueo REAL lo pone Videos-Backend (deny-by-host + CORS); esto es la cara.
// Override para probar en local: ?modo=cliente | ?modo=staff (persiste en la pestaña).
function getModoApp() {
  try {
    const q = new URLSearchParams(window.location.search).get('modo');
    if (q === 'cliente' || q === 'staff') sessionStorage.setItem('modo_app_demo', q);
    const forced = sessionStorage.getItem('modo_app_demo');
    if (forced === 'cliente' || forced === 'staff') return forced;
    const host = (window.location.hostname || '').toLowerCase();
    if (host.startsWith('canal.')) return 'cliente';
  } catch (e) { /* entorno raro → staff por defecto */ }
  return 'staff';
}

// ── Marca por dominio (Ann 2026-07-13): en canal.* la pestaña es del CANAL ──
// (título "Canal Pilar Domínguez MEFIR" + favicon MPD + meta para buscadores).
// En crm.* no se toca nada. Se aplica una sola vez al cargar la app.
function aplicarMarcaCanal() {
  try {
    if (getModoApp() !== 'cliente') return;
    document.title = 'Canal Pilar Domínguez MEFIR';
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.rel = rel; document.head.appendChild(el); }
      el.href = href;
    };
    setLink('icon', '/favicon-mpd.png');
    setLink('apple-touch-icon', '/apple-touch-icon-mpd.png');
    const setMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description',
      'Canal Pilar Domínguez · MEFIR: clases online del Método Pilar Domínguez, rutina semanal guiada, contenido terapéutico y fisioterapia. Hazte socia del Club.');
    setMeta('property', 'og:title', 'Canal Pilar Domínguez MEFIR');
    setMeta('property', 'og:description',
      'Clases online del Método Pilar Domínguez: rutina semanal, contenido terapéutico y fisioterapia MEFIR.');
    setMeta('property', 'og:site_name', 'Canal Pilar Domínguez MEFIR');
  } catch (e) { /* nunca romper la app por la marca */ }
}
aplicarMarcaCanal();

// Interceptor de REQUEST: Inyecta el token en CADA petición
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de RESPONSE: Maneja errores de autenticación y validación
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // BLINDAJE F1 · sesión única: otro login del titular expulsó esta sesión
      // (token_version). Mensaje 1 + cierre de sesión + vuelta al login.
      if (error.response?.data?.detail?.code === 'SESION_EXPULSADA') {
        // Incidente Ann 12-sep-2026: una pestaña VIEJA (campana, cada 30 s) con token
        // caducado borraba el token NUEVO y echaba de la pestaña buena, en bucle.
        // Solo se cierra si el 401 corresponde al token vigente (blindaje intacto).
        if (debeCerrarPorExpulsion(tokenDePeticion(error.config), localStorage.getItem('token'))) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          toast.error(error.response.data.detail.message, { duration: 8000 });
          setTimeout(() => { window.location.href = '/'; }, 1500);
        }
      } else {
        // Diagnóstico: registrar QUÉ llamada dio 401 y con qué motivo, para saber
        // si a Ann la echan por red, backend u otra cosa. NO limpiamos (evita bucles).
        const url = error.config?.url || '(url desconocida)';
        const detalle = error.response?.data?.detail?.message || error.response?.data?.detail || '(sin detalle)';
        console.warn(`[SESION] 401 en ${url} — motivo: ${detalle}. No se cierra sesión automáticamente.`);
      }
    } else if (error.response?.status === 422 && Array.isArray(error.response?.data?.detail)) {
      // Prevenir cuelgue de "Objects are not valid as React child" en los toast.error
      try {
        const firstError = error.response.data.detail[0];
        error.response.data.detail = `Error de validación: ${firstError.msg}`;
      } catch (e) {
        error.response.data.detail = 'Error de validación en los datos enviados';
      }
    }
    return Promise.reject(error);
  }
);

// Landing ÚNICA del cliente: el menú del Canal Pilar Domínguez (gestor de cambios · canal
// de vídeos · perfil · clínica). Se RETIRAN el selector legacy 'Mi salud / Mis clases'
// (PlataformSelector) y el PatientDashboard legacy: ya no se usan (Ann 2026-06-30).
function ClientRouter({ user, onLogout }) {
  return <CanalPilarDashboard user={user} onLogout={onLogout} />;
}

function AppRoutes() {
  const { user, loading, login, logout, logoutLocal } = useAuth();

  const handleLogin = async (email, password) => {
    try {
      await login(email, password);
      // Red de seguridad del circuito Club (Ann 2026-07-19): si la clienta pagó
      // en WordPress pero NO pasó por "Pago recibido", el pago no quedó anotado
      // en el CRM ni salió tu aviso/su bienvenida. Al entrar, preguntamos a la
      // plataforma y, si paga, se registra + avisa + da la bienvenida (todo
      // idempotente). Deny-by-default: para no-Club / ya-activos es un no-op
      // barato (corta antes de consultar). Fire-and-forget: nunca afecta al login.
      confirmarPago().catch(() => {});
    } catch (error) {
      throw error;
    }
  };

  const handleLogout = () => {
    logout();
  };

  // Cierre automático por INACTIVIDAD (Ann 18-jul): en un ordenador compartido, si
  // te dejas la sesión abierta, a los 30 min sin tocar nada se cierra sola (logout
  // real en servidor). Así el siguiente que se siente NO entra como tú.
  useEffect(() => {
    if (!user) return undefined;
    // En LOCAL (desarrollo) NO cerramos por inactividad: es molesto tener que
    // volver a entrar en cada prueba. En prod sigue igual (30 min).
    if (typeof window !== 'undefined' && ['localhost','127.0.0.1'].includes(window.location.hostname)) return undefined;
    // Ann 12-sep-2026: el personal trabaja con la ficha abierta y leyendo; 30 min era
    // demasiado poco. Clienta en ordenador compartido: se mantiene en 30 min.
    const MS = (user.role === 'cliente' ? 30 : 8 * 60) * 60 * 1000;   // 30 min / 8 h
    let timer;
    // INCIDENTE 2026-09-08 (Ann): «a mitad de la clase les echa y tienen que volver a entrar».
    // Ver un vídeo del canal NO genera ratón/teclado/scroll, así que a los 30 min el
    // temporizador cerraba la sesión en plena clase. Regla: mientras haya un vídeo o audio
    // REPRODUCIÉNDOSE en la página, la sesión está activa. La protección del ordenador
    // compartido (30 min sin tocar nada Y sin nada reproduciéndose) se mantiene intacta.
    const hayMediaReproduciendose = () =>
      Array.from(document.querySelectorAll('video, audio')).some((m) => !m.paused && !m.ended && m.readyState > 2);
    const cerrarPorInactividad = () => {
      if (hayMediaReproduciendose()) { reiniciar(); return; }   // clase en curso: no se echa a nadie
      // Cierre SOLO de este navegador: protege el ordenador compartido, pero NO anula
      // las demás sesiones del titular (antes llamaba al logout del servidor y echaba
      // a Ann de la pestaña en la que estaba trabajando).
      logoutLocal();
    };
    const reiniciar = () => {
      clearTimeout(timer);
      timer = setTimeout(cerrarPorInactividad, MS);
    };
    const eventos = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    eventos.forEach((ev) => window.addEventListener(ev, reiniciar, { passive: true }));
    // Los eventos de <video>/<audio> no burbujean hasta window: se escuchan en captura sobre document.
    const eventosMedia = ['play', 'playing', 'seeking', 'volumechange'];
    eventosMedia.forEach((ev) => document.addEventListener(ev, reiniciar, true));
    reiniciar();
    return () => {
      clearTimeout(timer);
      eventos.forEach((ev) => window.removeEventListener(ev, reiniciar));
      eventosMedia.forEach((ev) => document.removeEventListener(ev, reiniciar, true));
    };
  }, [user, logoutLocal]);

  const modo = getModoApp();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />
      <GlobalUploadProgress />
      {modo === 'cliente' ? (
        /* ───── MODO CLIENTE (canal.*) · SOLO experiencia de cliente ─────
           El back-office (admin, contabilidad, clientes, kioskos…) NO se monta.
           Cualquier URL desconocida o de admin → home de cliente. */
        <Routes>
          <Route
            path="/"
            element={
              user && user.role === 'cliente'
                ? <Navigate to="/mi-espacio" replace />
                : (() => {
                  // PUERTA DE CLIENTA (Ann 2026-07-16): si este navegador ya entró
                  // como clienta (marca local, sin datos personales), la home lleva
                  // DIRECTA al formulario de acceso — la landing es el escaparate
                  // para quien aún no es clienta. Con ?ver=canal se enseña la
                  // landing igualmente (enseñar la plataforma / volver a verla).
                  let clienta = false;
                  try { clienta = localStorage.getItem('canal_clienta') === '1'; } catch (e) { /* noop */ }
                  const quiereVerla = new URLSearchParams(window.location.search).has('ver');
                  return (clienta && !quiereVerla)
                    ? <Navigate to="/entrar?puerta=canal" replace />
                    : (
                      <CanalLanding
                        onIniciarSesion={() => { window.location.href = '/entrar?puerta=canal'; }}
                        onEmpezar={(em) => { window.location.href = `/onboarding?email=${encodeURIComponent(em || '')}`; }}
                      />
                    );
                })()
            }
          />
          <Route
            path="/entrar"
            element={
              user && user.role === 'cliente'
                ? <Navigate to="/mi-espacio" replace />
                : <Login onLogin={handleLogin} />
            }
          />
          {/* PÁGINA PÚBLICA DE INSCRIPCIÓN (encargo Guardián 2026-07-19): URL propia
              para el botón «Inscribirme» de la web del método. Directa SIEMPRE (la
              marca canal_clienta solo actúa en «/», nunca aquí). */}
          {/* ACEPTAR INVITACIÓN al Canal (frente invitaciones · 2026-07-19): el email
              lleva aquí con ?token=…; antes no había ruta → página en blanco.
              Directa SIEMPRE (no pasa por la marca canal_clienta, que solo actúa en «/»). */}
          <Route path="/canal/invitacion" element={<InvitacionCanal onLogin={handleLogin} />} />
          <Route path="/inscribirme" element={<InscripcionCanal onEmpezar={(em) => { window.location.href = `/onboarding?email=${encodeURIComponent(em || '')}`; }} onIniciarSesion={() => { window.location.href = '/entrar'; }} />} />
          <Route path="/apuntarme" element={<InscripcionCanal onEmpezar={(em) => { window.location.href = `/onboarding?email=${encodeURIComponent(em || '')}`; }} onIniciarSesion={() => { window.location.href = '/entrar'; }} />} />
          {/* Formulario de alta SUELTO (Ann 2026-07-13): solo la tarjeta; al enviar avanza al
              alta del Club (NO vuelve a la home). */}
          <Route path="/canal-alta" element={<CanalAltaForm />} />
          {/* Circuito de suscripción: alta CLUB (wizard 5 pasos) → onboarding → login → mi-espacio */}
          <Route path="/club-alta" element={<ClubAlta onListo={() => { window.location.href = `/onboarding${window.location.search || ''}`; }} />} />
          <Route path="/registro" element={<RegistroCanal onListo={() => { window.location.href = `/onboarding${window.location.search || ''}`; }} />} />
          <Route path="/alta-funnel-preview" element={<AltaFunnelCanal email={new URLSearchParams(window.location.search).get('email') || 'tu@email.com'} onFinish={() => { window.location.href = '/pago-resultado'; }} maqueta />} />
          <Route path="/verificar-email" element={<VerificarEmail onContinuar={() => { const t = new URLSearchParams(window.location.search).get('token') || ''; window.location.href = `/onboarding?token=${encodeURIComponent(t)}`; }} onReenviar={() => { window.location.href = '/'; }} />} />
          <Route path="/pago-resultado" element={<PagoResultado onEntrar={() => { window.location.href = '/mi-espacio'; }} onCompletarPerfil={() => { window.location.href = '/completa-perfil'; }} onReintentar={() => { window.location.href = '/alta-funnel-preview'; }} onVolver={() => { window.location.href = '/'; }} />} />
          <Route path="/completa-perfil" element={<CompletaPerfil onGuardar={() => { window.location.href = '/mi-espacio'; }} onAhoraNo={() => { window.location.href = '/mi-espacio'; }} />} />
          <Route path="/condiciones" element={<LegalCanal tipo="condiciones" />} />
          <Route path="/privacidad" element={<LegalCanal tipo="privacidad" />} />
          {/* Embudo A (hacia el pago): crea contraseña → verifica email → (pago) */}
          <Route
            path="/onboarding"
            element={
              <AltaFunnelCanal
                email={new URLSearchParams(window.location.search).get('email') || 'tu@email.com'}
                onFinish={() => { window.location.href = '/entrar'; }}
              />
            }
          />
          <Route
            path="/mi-espacio"
            element={
              user && user.role === 'cliente'
                ? <ClientRouter user={user} onLogout={handleLogout} />
                : <Navigate to="/" replace />
            }
          />
          <Route path="/client" element={<Navigate to="/mi-espacio" replace />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/forgot-password"
            element={!user ? <Login onLogin={login} /> : <Navigate to="/" replace />}
          />
          {/* Formulario público que el cliente recibe por email (token) — es de cliente. */}
          <Route path="/paciente/cuestionario/:token" element={<TabletCuestionario />} />
          {/* Previews de cliente (temporales). */}
          <Route path="/login-preview" element={<Login onLogin={async () => { alert('Preview: aquí entraría de verdad.'); }} />} />
          <Route
            path="/onboarding-preview"
            element={
              <OnboardingBienvenida
                email={new URLSearchParams(window.location.search).get('email') || 'didizuib@gmail.com'}
                acceso={{ presencial: true, canal: true, fisio: true, clinica: true }}
                pasoInicial={new URLSearchParams(window.location.search).get('paso') === 'tutorial' ? 2 : 0}
                onFinish={() => { window.location.href = '/'; }}
              />
            }
          />
          <Route path="/registro-preview" element={<RegistroCanal onListo={() => { window.location.href = '/'; }} />} />
          <Route path="/recuperar-preview" element={<RecuperacionPasswordDemo onSalir={() => { window.location.href = '/'; }} />} />
          <Route path="/club-alta-preview" element={<ClubAlta onListo={() => { window.location.href = '/onboarding-preview'; }} />} />
          <Route path="/canal-landing-preview" element={<CanalLanding onIniciarSesion={() => { window.location.href = '/login-preview?puerta=canal'; }} onEmpezar={(em) => { window.location.href = `/alta-funnel-preview?email=${encodeURIComponent(em || '')}`; }} maqueta />} />
          {/* TODO lo demás (incl. cualquier ruta de back-office) → home de cliente.
              No se renderiza back-office bajo canal.* */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : (
        /* ───── MODO STAFF (crm.* / localhost) · sin cambios ───── */
      <Routes>
        {/* ACEPTAR INVITACIÓN al Canal · TAMBIÉN aquí (Ann 2026-07-20, crítico).
            El correo manda a crm.metodopilardominguez.com, que NO empieza por
            «canal.» → la app entra en modo staff, y este bloque no tenía la ruta
            ni ruta de rescate: React no pintaba nada → PÁGINA EN BLANCO, sin una
            sola petición al servidor. Ninguna invitada podía entrar.
            La pantalla es pública (el token manda, no la sesión), así que no se
            pone ningún guard: ponerlo dejaría fuera justo a quien viene del
            correo sin sesión. Verificado: el mismo enlace con ?modo=cliente sí
            funcionaba, y esa fue la prueba de la causa. */}
        <Route path="/canal/invitacion" element={<InvitacionCanal onLogin={handleLogin} />} />
        {/* PUERTA DE ENTRADA de las clientas. Tampoco estaba aquí, y la pantalla
            de invitación termina justo mandando a «/entrar?puerta=canal»: sin
            esta ruta, aceptar la invitación llevaba a OTRA página en blanco.
            Misma lógica que en la cara de clientas. */}
        <Route
          path="/entrar"
          element={
            user && user.role === 'cliente'
              ? <Navigate to="/mi-espacio" replace />
              : <Login onLogin={handleLogin} />
          }
        />
        <Route
          path="/"
          element={
            user ? (
              (esPersonal(user) || esColaborador(user)) ? (
                <Navigate to="/admin" replace />
              ) : (
                <Navigate to="/mi-espacio" replace />
              )
            ) : (
              <Login onLogin={handleLogin} />
            )
          }
        />
        <Route
          path="/admin"
          element={
            user && (esPersonal(user) || esColaborador(user)) ? (
              <AdminDashboard user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/mi-espacio"
          element={
            user && user.role === 'cliente' ? (
              <ClientRouter user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* Mantener /client por compatibilidad */}
        <Route
          path="/client"
          element={
            user && user.role === 'cliente' ? (
              <Navigate to="/mi-espacio" replace />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/reset-password"
          element={<ResetPassword />}
        />
        {/* PUENTE-WP-CLUB · SSO WordPress → CRM (canjea ?token= por sesión propia) */}
        <Route
          path="/auth/wordpress-token"
          element={<WordpressTokenBridge />}
        />
        <Route
          path="/canal-subir"
          element={<CanalSubir />}
        />
        <Route
          path="/forgot-password"
          element={
            !user ? (
              <Login onLogin={login} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* Tablet route - no auth required */}
        <Route
          path="/tablet/cuestionario/:codigo"
          element={<TabletCuestionario />}
        />
        {/* Email link route - acceso público por token (Opción C · Ann 2026-05-19) */}
        <Route
          path="/paciente/cuestionario/:token"
          element={<TabletCuestionario />}
        />
        {/* Nueva ficha aislada · PR-IS-CIERRE-PRG-1 · F3 (Ann 2026-05-24)
            envuelta en ClientProfileErrorBoundary para que cualquier crash
            de render no deje la página completamente en blanco. */}
        <Route
          path="/admin/cliente-nuevo"
          element={
            esPersonal(user) ? (
              <ClientProfileErrorBoundary>
                <ClientNewProfile />
              </ClientProfileErrorBoundary>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* Ficha del Trabajador (Laboral → Profesionales · tema verde) · página NUEVA */}
        <Route
          path="/admin/empleado-ficha"
          element={
            esPersonal(user) ? (
              <ClientProfileErrorBoundary>
                <EmpleadoFicha />
              </ClientProfileErrorBoundary>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* Cobros pendientes — página propia */}
        <Route
          path="/admin/cobros-pendientes"
          element={
            user && esPersonal(user) ? (
              <CobrosPendientesPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* Control de Asistencia (kiosko "pasar lista") — gate fino al permiso
            asistencia.control.pasar-lista dentro del propio componente. */}
        <Route
          path="/control-asistencia"
          element={
            user && esPersonal(user) ? (
              <ControlAsistenciaPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* VISTA EN SALA (kiosko de 2 puertas) — pantalla cerrada de la tablet de
            sala. NO requiere usuario CRM: la Puerta 1 (sala) es el gate. El token
            de sesión de sala lo define/valida el Guardián (deny-by-default,
            FRONTERA en api/controlAsistencia.js). docs/SPEC_VISTA_EN_SALA.md */}
        <Route path="/vista-en-sala" element={<VistaSalaShell />} />
        {/* PREVIEW del login re-estilizado (temporal, para verlo sin cerrar sesión). */}
        <Route path="/login-preview" element={<Login onLogin={async () => { alert('Preview: aquí entraría de verdad.'); }} />} />
        <Route path="/alta-funnel-preview" element={<AltaFunnelCanal email={new URLSearchParams(window.location.search).get('email') || 'tu@email.com'} onFinish={() => { window.location.href = '/pago-resultado'; }} maqueta />} />
        <Route path="/verificar-email" element={<VerificarEmail onContinuar={() => { window.location.href = '/alta-funnel-preview'; }} onReenviar={() => { window.location.href = '/canal-landing-preview'; }} maqueta />} />
        <Route path="/pago-resultado" element={<PagoResultado onEntrar={() => { window.location.href = '/canal-landing-preview'; }} onCompletarPerfil={() => { window.location.href = '/completa-perfil'; }} onReintentar={() => { window.location.href = '/alta-funnel-preview'; }} onVolver={() => { window.location.href = '/canal-landing-preview'; }} />} />
        <Route path="/completa-perfil" element={<CompletaPerfil onGuardar={() => { window.location.href = '/canal-landing-preview'; }} onAhoraNo={() => { window.location.href = '/canal-landing-preview'; }} />} />
        <Route path="/condiciones" element={<LegalCanal tipo="condiciones" />} />
        <Route path="/privacidad" element={<LegalCanal tipo="privacidad" />} />
        {/* PREVIEW registro y recuperación de contraseña (temporal). */}
        <Route path="/registro-preview" element={<RegistroCanal onListo={() => { window.location.href = '/login-preview'; }} />} />
        <Route path="/recuperar-preview" element={<RecuperacionPasswordDemo onSalir={() => { window.location.href = '/login-preview'; }} />} />
        <Route path="/club-alta-preview" element={<ClubAlta onListo={() => { window.location.href = '/onboarding-preview'; }} />} />
        <Route path="/canal-landing-preview" element={<CanalLanding onIniciarSesion={() => { window.location.href = '/login-preview?puerta=canal'; }} onEmpezar={(em) => { window.location.href = `/alta-funnel-preview?email=${encodeURIComponent(em || '')}`; }} maqueta />} />
        {/* PREVIEW del menú de llegada (QA bilingüe/detector de fugas): usuaria FICTICIA,
            los fetch fallan sin sesión y el menú degrada a sus valores por defecto. */}
        <Route path="/canal-menu-preview" element={<CanalPilarDashboard user={{ nombre: 'Prova' }} onLogout={() => { window.location.href = '/login-preview'; }} />} />
        {/* PREVIEW de Mi Perfil (QA bilingüe/detector de fugas): usuaria ficticia, fetch degradan. */}
        <Route path="/miperfil-preview" element={<MiPerfil user={{ nombre: 'Prova' }} onBack={() => {}} onLogout={() => {}} />} />
        {/* PREVIEW del Gestor de Cambios (QA bilingüe/detector de fugas · F2): usuaria ficticia,
            fetch degradan. ?vista=foto|is|deuda para escanear pantallas condicionales. */}
        <Route path="/gestor-preview" element={(() => {
          const vista = new URLSearchParams(window.location.search).get('vista');
          if (vista === 'foto') return <FotoPerfilObligatoria user={{ nombre: 'Prova' }} onFotoSubida={() => {}} motivoRechazo="Prova" />;
          const u = { nombre: 'Prova', primer_apellido: '', servicio: 'fijo' };
          if (vista === 'is') u.incidencia_salud_activa = true;
          if (vista === 'deuda') u.tiene_deuda = true;
          return <ClientDashboard user={u} onLogout={() => {}} onUserUpdate={() => {}} onBackToSelector={() => {}} />;
        })()} />
        {/* PREVIEW del canal de vídeos interior (QA bilingüe/detector de fugas · F3):
            clienta ficticia; sin backend los fetch degradan (canal no disponible),
            el detector los mockea con page.route. */}
        <Route path="/canal-videos-preview" element={<VisionClienteTab clientMode={true} clienteNombre="Prova" />} />
        {/* PREVIEW QA del reproductor PROPIO (vídeo de demostración público, sin datos reales) */}
        <Route path="/reproductor-preview" element={<ReproductorPreview />} />
        {/* PREVIEW del onboarding de primera vez (temporal, para enseñárselo a Ann).
            Público: no requiere login ni ser cliente. /onboarding-preview */}
        <Route
          path="/onboarding-preview"
          element={
            <OnboardingBienvenida
              email={new URLSearchParams(window.location.search).get('email') || 'didizuib@gmail.com'}
              acceso={{ presencial: true, canal: true, fisio: true, clinica: true }}
              pasoInicial={new URLSearchParams(window.location.search).get('paso') === 'tutorial' ? 2 : 0}
              onFinish={() => { window.location.href = '/'; }}
            />
          }
        />
        {/* RED DE SEGURIDAD (Ann 2026-07-20): este bloque NO tenía ruta de
            rescate, así que CUALQUIER dirección desconocida dejaba la pantalla
            en blanco, sin un solo mensaje. Así se perdieron las invitaciones.
            La cara de clientas ya la tenía; aquí faltaba. Nunca más en blanco:
            si la dirección no existe, se vuelve al principio. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* LanguageProvider DENTRO de AuthProvider: el idioma_preferido del cliente
            logueado manda sobre el localStorage del invitado (F0 bilingüe ES/CA). */}
        <LanguageProvider>
          <VideoUploadProvider>
            <AppRoutes />
          </VideoUploadProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
